import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Checkbox, Dialog, useToast } from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import { useElevation } from '../../../../components/useElevation.js';
import { formaterDate } from '../../../../lib/format.js';
import {
  annulerLot,
  lireAnnulation,
  ordreInverseDuPlan,
  raisonPlan,
  type FicheGardee,
  type RapportAnnulation,
} from '../../../../lib/plan.js';
import { lotAnnule, lotExecute, lotScelle, type LotReprise } from '../../../../lib/reprise.js';
import { TON_ETAT_LOT, etatAfficheLot, messageReprise, texteRaisonTravail } from '../messages.js';
import pageStyles from '../../../../styles/page.module.css';
import reprise from '../Reprise.module.css';
import styles from './Plan.module.css';
import { ProgressionTravail } from './ProgressionTravail.js';
import { ReferenceOrigine } from './ReferenceOrigine.js';

/** Combien de fiches gardées on liste avant de renvoyer au rapport complet. */
const LIMITE_GARDEES = 50;

/** Les refus de la route qui tiennent à la galerie : le bouton se grise, avec sa raison. */
const RAISONS_QUI_GRISENT: readonly string[] = [
  'galerie_en_service',
  'plan_actif',
  'reprise_en_cours',
];

/** La raison du bouton grisé, en une phrase ; un code inconnu se dit comme une erreur de l'API. */
function texteRefus(t: TFunction, raison: string): string {
  const cles: Record<string, string> = {
    galerie_en_service: 'plan.annulation.galerieEnService',
    plan_actif: 'plan.annulation.planActif',
    reprise_en_cours: 'plan.annulation.importEnCours',
  };
  return String(t(cles[raison] ?? `plan.erreurs.${raison}`, { defaultValue: raison }));
}

function idCourt(id: string): string {
  return id.length > 10 ? `…${id.slice(-8)}` : id;
}

/** Le motif d'une fiche gardée, en une phrase (`reprise.md`, « Retenue par »). */
function MotifGarde({ motif }: { motif: string }): JSX.Element {
  const { t } = useTranslation('console');
  const nu = motif.startsWith('reference:') ? motif.slice('reference:'.length) : motif;
  if (['modifie', 'etat_inconnu', 'autre_lot', 'variantes'].includes(nu))
    return <>{t(`plan.annulation.rapport.motifs.${nu}`)}</>;
  if (motif.startsWith('reference:')) {
    const type = String(
      t(`plan.annulation.rapport.references.${nu}`, { defaultValue: nu.replaceAll('_', ' ') }),
    );
    return <>{t('plan.annulation.rapport.motifs.referencee', { type })}</>;
  }
  return <>{t('plan.annulation.rapport.motifInconnu', { motif })}</>;
}

function RapportAnnulationVue({
  rapport,
  lot,
}: {
  rapport: RapportAnnulation;
  lot: LotReprise;
}): JSX.Element {
  const { t } = useTranslation('console');
  // Les compteurs sont ceux de l'API (la fiche du lot) ; le rapport ne sert qu'au détail.
  const compteurs = lot.annulation?.compteurs ?? {
    supprimes: rapport.supprimes.length,
    conserves: rapport.conserves.length,
    dejaAbsents: rapport.dejaAbsents.length,
    nonDefaites: rapport.nonDefaites,
  };
  const modele = (valeur: string): string =>
    String(t(`plan.annulation.rapport.modeles.${valeur}`, { defaultValue: valeur }));
  const parType = new Map<string, number>();
  for (const fiche of rapport.supprimes)
    parType.set(fiche.modele, (parType.get(fiche.modele) ?? 0) + 1);
  const gardees: FicheGardee[] = rapport.conserves.slice(0, LIMITE_GARDEES);

  return (
    <div className={styles.rapportAnnulation}>
      <h3>{t('plan.annulation.rapport.titre')}</h3>
      <dl
        className={reprise.compteurs}
        role="group"
        aria-label={t('plan.annulation.rapport.compteurs')}
      >
        <div data-compteur="supprimes">
          <dt>{t('plan.annulation.rapport.supprimes')}</dt>
          <dd>{compteurs.supprimes}</dd>
        </div>
        <div data-compteur="conserves">
          <dt>{t('plan.annulation.rapport.conserves')}</dt>
          <dd>{compteurs.conserves}</dd>
        </div>
        <div data-compteur="nonDefaites">
          <dt>{t('plan.annulation.rapport.nonDefaites')}</dt>
          <dd>{compteurs.nonDefaites}</dd>
        </div>
        <div data-compteur="dejaAbsents">
          <dt>{t('plan.annulation.rapport.dejaAbsents')}</dt>
          <dd>{compteurs.dejaAbsents}</dd>
        </div>
        <div data-compteur="medias">
          <dt>{t('plan.annulation.rapport.medias')}</dt>
          <dd>{rapport.objetsRetires}</dd>
        </div>
      </dl>
      <p className={reprise.discret}>
        {t('plan.annulation.rapport.mediasAide')} {t('plan.annulation.rapport.nonDefaitesAide')}
      </p>
      {rapport.sansCible > 0 ? (
        <p className={reprise.avertissement} role="note">
          {t('plan.annulation.rapport.sansCible', { count: rapport.sansCible })}
        </p>
      ) : null}

      {parType.size > 0 ? (
        <>
          <h4>{t('plan.annulation.rapport.supprimesParType')}</h4>
          <ul className={styles.cles}>
            {[...parType.entries()].map(([type, nombre]) => (
              <li key={type}>
                {modele(type)} : <strong>{nombre}</strong>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h4>{t('plan.annulation.rapport.gardees')}</h4>
      {gardees.length === 0 ? (
        <p className={reprise.discret}>{t('plan.annulation.rapport.aucuneGardee')}</p>
      ) : (
        <ul className={styles.gardees} tabIndex={0}>
          {gardees.map((fiche) => (
            <li key={`${fiche.modele}:${fiche.id}`} className={styles.gardee}>
              <span>
                <strong title={fiche.id}>{modele(fiche.modele)}</strong>
                <span className={styles.designation}>
                  {fiche.ligne === undefined ? null : (
                    <span>{t('plan.annulation.rapport.ligne', { numero: fiche.ligne })}</span>
                  )}
                  {fiche.refOrigine === undefined ? null : (
                    <ReferenceOrigine valeur={fiche.refOrigine} />
                  )}
                  {fiche.ligne === undefined && fiche.refOrigine === undefined ? (
                    <span className={styles.identifiant} title={fiche.id}>
                      {idCourt(fiche.id)}
                    </span>
                  ) : null}
                </span>
              </span>
              <ul className={styles.motifs}>
                {fiche.motifs.map((motif) => (
                  <li key={motif}>
                    <MotifGarde motif={motif} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {rapport.conserves.length > LIMITE_GARDEES ? (
        <p className={reprise.discret}>
          {t('plan.annulation.rapport.et', { count: rapport.conserves.length - LIMITE_GARDEES })}
        </p>
      ) : null}
    </div>
  );
}

function RapportDeLAnnulation({
  idGalerie,
  lot,
}: {
  idGalerie: string;
  lot: LotReprise;
}): JSX.Element {
  const { t } = useTranslation('console');
  const rapport = useQuery({
    queryKey: [
      'console',
      'galerie',
      idGalerie,
      'reprise',
      'annulation',
      lot.id,
      lot.annulation?.faitLe,
    ],
    queryFn: () => lireAnnulation(idGalerie, lot.id),
    staleTime: Infinity,
  });
  if (rapport.isLoading)
    return <p className={reprise.discret}>{t('plan.annulation.rapport.chargement')}</p>;
  if (rapport.isError || !rapport.data)
    return (
      <p className={reprise.erreur} role="alert">
        {messageReprise(t, rapport.error) ?? t('plan.annulation.rapport.indisponible')}
      </p>
    );
  return <RapportAnnulationVue rapport={rapport.data} lot={lot} />;
}

/**
 * L'annulation d'un lot et le scellement vus depuis sa fiche (ARTO-132). Le
 * bouton « Annuler ce lot » n'est proposé qu'à qui a `console:rollback-import`
 * (la direction de Venio), jamais sur un lot scellé, déjà annulé ou qui se
 * découpe en morceaux, et pas tant qu'un plan occupe la galerie. Elle suit
 * l'annulation (progression, puis rapport) et dit les refus de l'API en clair.
 */
export function AnnulationBloc({
  idGalerie,
  lot,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  onLot: (lot: LotReprise) => void;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const { toast } = useToast();
  const elevation = useElevation();
  const [ouvert, setOuvert] = useState(false);
  const [prevenue, setPrevenue] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Le refus que la route vient de rendre : plus récent que la fiche du lot, qui l'a lue avant.
  const [refusServeur, setRefusServeur] = useState<string | null>(null);

  const droit = peut('console:rollback-import');

  const annulation = lot.annulation ?? null;
  const scelle = lotScelle(lot);
  const annule = lotAnnule(lot);
  const enCours = annulation?.etat === 'demandee' || annulation?.etat === 'en_cours';
  const morceaux = lot.morceaux?.liste ?? [];
  // Un lot qui se découpe s'annule morceau par morceau : le guide ne sert qu'une fois l'un d'eux exécuté.
  const seDecoupe = morceaux.length > 0;
  const guideMorceaux = seDecoupe && morceaux.some((morceau) => lotExecute(morceau));

  const demander = useMutation({
    mutationFn: () => elevation.executer(() => annulerLot(idGalerie, lot.id)),
    onSuccess: (suite) => {
      setOuvert(false);
      setPrevenue(false);
      setErreur(null);
      toast({
        title: t('plan.annulation.suivi.demandee', {
          par: suite.annulation?.par ?? '',
          date: formaterDate(suite.annulation?.demandeLe ?? null, '—'),
        }),
        closeLabel: t('fiche.domaines.fermer'),
      });
      onLot(suite);
    },
    onError: (cause) => {
      setOuvert(false);
      setPrevenue(false);
      const raison = raisonPlan(cause);
      if (raison !== null && RAISONS_QUI_GRISENT.includes(raison)) setRefusServeur(raison);
      setErreur(messageReprise(t, cause));
    },
  });

  const peutProposer = droit && lotExecute(lot) && !scelle && !annule && !enCours && !seDecoupe;
  // Ce que l'API dit d'avance (`annulable`) ; la route reste l'autorité.
  const refus = refusServeur ?? (lot.annulable?.possible === false ? lot.annulable.raison : null);
  const nature = String(t(`reprise.natures.${lot.nature}`));

  // Rien à dire : ni geste possible, ni état à montrer.
  if (!scelle && !annulation && !guideMorceaux && !peutProposer) return null;

  const etat = etatAfficheLot(lot);
  return (
    <section className={pageStyles.panneau} aria-labelledby="annulation-titre">
      <div className={reprise.pile}>
        <div className={reprise.rangee}>
          <h3 id="annulation-titre" className={reprise.sousTitre}>
            {t('plan.annulation.titre')}
          </h3>
          {scelle || annule || enCours ? <Badge tone={etat.ton}>{t(etat.cle)}</Badge> : null}
        </div>

        {scelle ? (
          <div className={styles.etat} data-ton="scelle">
            <p>
              <strong>{t('plan.annulation.scelle.titre')}</strong>
            </p>
            <p>
              {t('plan.annulation.scelle.texte', {
                date: formaterDate(lot.scelleLe ?? lot.termineLe, '—'),
              })}
            </p>
          </div>
        ) : null}

        {annule ? (
          <div className={styles.etat} data-ton="annule">
            <p>
              <strong>{t('plan.annulation.lotAnnule.titre')}</strong>
            </p>
            <p>{t('plan.annulation.lotAnnule.texte')}</p>
            {annulation ? (
              <p className={reprise.discret}>
                {t('plan.annulation.suivi.faite', {
                  par: annulation.par,
                  date: formaterDate(annulation.faitLe ?? annulation.demandeLe, '—'),
                })}
              </p>
            ) : null}
          </div>
        ) : null}

        {annulation && !annule ? (
          <div
            className={styles.etat}
            data-ton={annulation.etat === 'echouee' ? 'echec' : undefined}
          >
            <p aria-live="polite" role={annulation.etat === 'echouee' ? 'alert' : undefined}>
              {t(`plan.annulation.suivi.${annulation.etat}`, {
                par: annulation.par,
                date: formaterDate(annulation.faitLe ?? annulation.demandeLe, '—'),
              })}
            </p>
            <ProgressionTravail travail={annulation.travail} actif={enCours} />
            {annulation.etat === 'echouee' && annulation.travail ? (
              <p className={reprise.discret}>{texteRaisonTravail(t, annulation.travail)}</p>
            ) : null}
          </div>
        ) : null}

        {annule ? <RapportDeLAnnulation idGalerie={idGalerie} lot={lot} /> : null}

        {guideMorceaux && !scelle ? (
          <div className={styles.etat}>
            <p>
              <strong>{t('plan.annulation.morceaux.titre')}</strong>
            </p>
            <p>{t('plan.annulation.morceaux.texte')}</p>
            {droit ? (
              <>
                <span className={reprise.discret}>{t('plan.annulation.morceaux.ordre')}</span>
                <ol className={styles.morceaux}>
                  {ordreInverseDuPlan(morceaux).map((morceau) => {
                    const nom = t('plan.annulation.morceaux.nom', {
                      nature: t(`reprise.natures.${morceau.nature}`),
                      rang: morceau.rang,
                    });
                    return (
                      <li key={morceau.id}>
                        <Link
                          className={reprise.lienLot}
                          to={`?lot=${encodeURIComponent(morceau.id)}`}
                          aria-label={t('plan.annulation.morceaux.ouvrir', {
                            nature: t(`reprise.natures.${morceau.nature}`),
                            rang: morceau.rang,
                          })}
                        >
                          {nom}
                        </Link>
                        <Badge tone={TON_ETAT_LOT[morceau.etat]}>
                          {t(`reprise.etats.${morceau.etat}`)}
                        </Badge>
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : null}
          </div>
        ) : null}

        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        {peutProposer ? (
          <>
            <div className={reprise.actions}>
              <Button
                variant={annulation?.etat === 'echouee' ? 'primary' : 'secondary'}
                disabled={refus !== null || demander.isPending}
                aria-describedby="annulation-aide"
                onClick={() => setOuvert(true)}
              >
                {annulation?.etat === 'echouee'
                  ? t('plan.annulation.rejouer')
                  : t('plan.annulation.bouton')}
              </Button>
            </div>
            {refus !== null ? (
              <p id="annulation-aide" className={reprise.avertissement} role="note">
                {texteRefus(t, refus)}
              </p>
            ) : (
              <p id="annulation-aide" className={reprise.discret}>
                {t('plan.annulation.texte')}
              </p>
            )}
          </>
        ) : null}

        <Dialog
          open={ouvert && !elevation.enElevation}
          onOpenChange={(estOuvert) => {
            setOuvert(estOuvert);
            if (!estOuvert) setPrevenue(false);
          }}
          title={t('plan.annulation.dialogue.titre')}
          description={t('plan.annulation.dialogue.intro')}
          closeLabel={t('plan.annulation.dialogue.garder')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOuvert(false)}>
                {t('plan.annulation.dialogue.garder')}
              </Button>
              <Button
                loading={demander.isPending}
                disabled={!prevenue}
                onClick={() => demander.mutate()}
              >
                {t('plan.annulation.dialogue.confirmer')}
              </Button>
            </>
          }
        >
          <div className={styles.effets}>
            <p className={reprise.discret}>
              {nature} · {t('reprise.fiche.lignes')} : {lot.lignes}
            </p>
            <div>
              <h4>{t('plan.annulation.dialogue.supprimeTitre')}</h4>
              <ul>
                <li>{t('plan.annulation.dialogue.supprime1')}</li>
                <li>{t('plan.annulation.dialogue.supprime2')}</li>
              </ul>
            </div>
            <div>
              <h4>{t('plan.annulation.dialogue.conserveTitre')}</h4>
              <ul>
                <li>{t('plan.annulation.dialogue.conserve1')}</li>
                <li>{t('plan.annulation.dialogue.conserve2')}</li>
                <li>{t('plan.annulation.dialogue.conserve3')}</li>
              </ul>
            </div>
            <div>
              <h4>{t('plan.annulation.dialogue.apresTitre')}</h4>
              <ul>
                <li>{t('plan.annulation.dialogue.apres1')}</li>
                <li>{t('plan.annulation.dialogue.apres2')}</li>
              </ul>
            </div>
            <p className={reprise.avertissement} role="note">
              {t('plan.annulation.dialogue.mfa')}
            </p>
            <Checkbox
              id="annulation-prevenue"
              checked={prevenue}
              onCheckedChange={(valeur) => setPrevenue(valeur === true)}
              label={t('plan.annulation.dialogue.prevenue')}
            />
            <p className={reprise.discret}>{t('plan.annulation.dialogue.prevenueAide')}</p>
          </div>
        </Dialog>
        {elevation.dialogue}
      </div>
    </section>
  );
}
